import type {
  GameContext,
  GameInstance,
  GameModule,
  PreloadContext,
} from '@arcraz/arcadia-types';
import { PirateFleetInstance } from './instance.js';
import { PALETTE_HINT } from './palette.js';

/**
 * PIRATE FLEET — fifth cartridge.
 *
 * Top-down naval combat with the **wind** as the distinct mechanic:
 * the player's max sailing speed depends on heading relative to wind,
 * forcing real navigation instead of point-and-shoot. Combat is via
 * port + starboard broadside cannons — turning to bring a side to bear
 * is the core combat verb.
 *
 * Ramming an enemy triggers boarding, which PRNG-routes 50/50 to either
 * a sword duel or an artillery mini-game. Win the mini-game for a big
 * score bonus + automatic enemy sink.
 *
 * Heritage: Sea Wolf (ARC 1976) naval shooting, Battlezone (ARC 1980)
 * "you are the vehicle" feel, Adventure (A2600) explore-fight-collect.
 */
const module_: GameModule = {
  id: 'pirate-fleet',
  title: 'PIRATE FLEET',
  manufacturer: 'ARCADIA AMUSEMENTS',
  year: 1984,
  genre: 'arena',
  resolution: { w: 480, h: 270 },
  pixelated: true,
  paletteHint: PALETTE_HINT,
  validatorVersion: 1,
  controls: [
    { keys: 'LEFT / RIGHT', action: 'TURN HELM' },
    { keys: 'UP', action: 'TRIM SAILS' },
    { keys: 'Z / X', action: 'FIRE BOTH BROADSIDES' },
    { keys: 'RAM ENEMY', action: 'BOARDING (Z STRIKE, X PARRY)' },
    { keys: 'Z', action: 'RESTART (AT GAME OVER)' },
  ],

  async preload(ctx: PreloadContext): Promise<void> {
    // Cannon fire: short noise + low square thump. Distinct from cartridge
    // crash sounds — this is *your* attack, not damage to you.
    ctx.registerSfx('cannon', {
      wave: 'noise',
      pitch: [{ t: 0, f: 120 }, { t: 140, f: 60 }],
      gain: [{ t: 0, g: 0 }, { t: 4, g: 0.35 }, { t: 140, g: 0.0 }],
    });
    // Wood crack — player takes damage from a cannonball.
    ctx.registerSfx('wood-crack', {
      wave: 'noise',
      pitch: [{ t: 0, f: 220 }, { t: 130, f: 100 }],
      gain: [{ t: 0, g: 0 }, { t: 6, g: 0.35 }, { t: 130, g: 0.0 }],
    });
    // Enemy ship sinks — long noise burst with descending pitch.
    ctx.registerSfx('sink', {
      wave: 'noise',
      pitch: [{ t: 0, f: 200 }, { t: 600, f: 30 }],
      gain: [{ t: 0, g: 0 }, { t: 12, g: 0.5 }, { t: 600, g: 0.0 }],
    });
    // Treasure pickup — bright chime.
    ctx.registerSfx('treasure', {
      wave: 'triangle',
      pitch: [{ t: 0, f: 880 }, { t: 80, f: 1320 }, { t: 160, f: 1760 }],
      gain: [{ t: 0, g: 0 }, { t: 6, g: 0.25 }, { t: 160, g: 0.0 }],
    });
    // Sail flap — brief whoosh when wind hits the player's sail (currently
    // a sentinel sting for heading transitions; full continuous wind hum
    // uses audio.loop() in the instance).
    ctx.registerSfx('sail-flap', {
      wave: 'noise',
      pitch: [{ t: 0, f: 400 }, { t: 200, f: 250 }],
      gain: [{ t: 0, g: 0 }, { t: 10, g: 0.15 }, { t: 200, g: 0.0 }],
    });
    // Boarding trigger — dramatic descending fanfare, signals mini-game.
    ctx.registerSfx('boarding', {
      wave: 'square',
      pitch: [
        { t: 0, f: 660 },
        { t: 100, f: 440 },
        { t: 200, f: 330 },
        { t: 350, f: 220 },
      ],
      gain: [{ t: 0, g: 0 }, { t: 10, g: 0.4 }, { t: 350, g: 0.0 }],
    });
    // Sword clash — sharp metallic.
    ctx.registerSfx('clash', {
      wave: 'square',
      pitch: [{ t: 0, f: 1320 }, { t: 60, f: 880 }],
      gain: [{ t: 0, g: 0 }, { t: 3, g: 0.3 }, { t: 60, g: 0.0 }],
    });
    // Player-strike-lands in duel — softer wood-thunk.
    ctx.registerSfx('strike', {
      wave: 'triangle',
      pitch: [{ t: 0, f: 440 }, { t: 60, f: 220 }],
      gain: [{ t: 0, g: 0 }, { t: 4, g: 0.3 }, { t: 60, g: 0.0 }],
    });
    // Artillery cannonball hits hull — wood crack variant.
    ctx.registerSfx('art-hit', {
      wave: 'noise',
      pitch: [{ t: 0, f: 180 }, { t: 200, f: 60 }],
      gain: [{ t: 0, g: 0 }, { t: 8, g: 0.45 }, { t: 200, g: 0.0 }],
    });
    // Artillery cannonball lands in water — duller splash.
    ctx.registerSfx('art-miss', {
      wave: 'noise',
      pitch: [{ t: 0, f: 350 }, { t: 250, f: 150 }],
      gain: [{ t: 0, g: 0 }, { t: 10, g: 0.2 }, { t: 250, g: 0.0 }],
    });
    // Boarding win — short triumphant chord.
    ctx.registerSfx('board-win', {
      wave: 'square',
      pitch: [
        { t: 0, f: 523 },
        { t: 80, f: 659 },
        { t: 160, f: 784 },
        { t: 240, f: 1046 },
      ],
      gain: [{ t: 0, g: 0 }, { t: 8, g: 0.35 }, { t: 240, g: 0.0 }],
    });
  },

  init(ctx: GameContext): GameInstance {
    return new PirateFleetInstance(ctx);
  },
};

export default module_;
